// =============================================================================
// FILE: packages/renderer/src/components/public/R2Companion.tsx
// PURPOSE: Persistent R2 Companion — a dockable, minimizable Emmy chatbox that
//          lives on the public landing page. Persists chat history, open /
//          minimized / "parked on side" state across page reloads via
//          localStorage. Streams from the SSE backend (/api/ai/chat) in the
//          unauthenticated landing context. Fires haptics + a scoped Warp Drive
//          starfield behind the bubbles while Emmy is thinking.
// =============================================================================

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Send, RotateCcw, Minus, PanelRightClose, PanelBottomClose, Sparkles, StopCircle } from 'lucide-react';
import { EMMY_SYSTEM_PROMPT, EMMY_NAME, EMMY_GREETING } from '@bim/shared/config/emmy.config.js';
import { playClick, playNav } from '../../lib/soundManager.js';
import { GlobalIntelligenceReveal } from './GlobalIntelligenceReveal';

// ── Types & persistence ───────────────────────────────────────────────────────

interface ChatTurn {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  streaming?: boolean;
}

const STORAGE_KEY = 'rcd2.companion.v1';

interface Persisted {
  open: boolean;
  parked: boolean;
  convId: string;
  turns: ChatTurn[];
}

function loadState(): Persisted {
  const fallback: Persisted = { open: false, parked: false, convId: crypto.randomUUID(), turns: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (!parsed || !Array.isArray(parsed.turns)) return fallback;
    // Sanitise any dangling streaming flags from a mid-stream reload.
    const turns = parsed.turns
      .filter((t): t is ChatTurn => !!t && (t.role === 'user' || t.role === 'assistant') && typeof t.content === 'string')
      .map((t) => ({ ...t, streaming: false }));
    return {
      open: Boolean(parsed.open),
      parked: Boolean(parsed.parked),
      convId: typeof parsed.convId === 'string' ? parsed.convId : crypto.randomUUID(),
      turns,
    };
  } catch {
    return fallback;
  }
}

function saveState(s: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      open: s.open,
      parked: s.parked,
      convId: s.convId,
      turns: s.turns.filter((t) => t.role !== 'system').map((t) => ({ id: t.id, role: t.role, content: t.content })),
    }));
  } catch { /* storage unavailable — session-only, graceful */ }
}

function haptic(): void {
  try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8); } catch { /* */ }
}

// ── Scoped mini Warp starfield (fires while streaming) ─────────────────────────

function MiniWarp({ active }: { active: boolean }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    interface S { nx: number; ny: number; z: number; size: number; hue: number; }
    const make = (far: boolean): S => {
      const a = Math.random() * Math.PI * 2;
      const spread = Math.random() * 0.45 + 0.02;
      return { nx: Math.cos(a) * spread, ny: Math.sin(a) * spread, z: far ? Math.random() : 0.85 + Math.random() * 0.15, size: Math.random() * 1.6 + 0.5, hue: 170 + Math.random() * 38 };
    };
    const stars = Array.from({ length: 90 }, () => make(true));
    let speed = 0;
    let raf = 0;
    let alive = true;
    const draw = (): void => {
      if (!alive) return;
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      if (canvas.width !== w || canvas.height !== h) { canvas.width = Math.max(w, 1); canvas.height = Math.max(h, 1); }
      const target = activeRef.current ? 0.02 : 0.0022;
      speed += (target - speed) * 0.05;
      ctx.fillStyle = `rgba(6,11,20,${activeRef.current ? 0.16 : 0.34})`;
      ctx.fillRect(0, 0, w, h);
      const cx = w * 0.5, cy = h * 0.5, fov = Math.min(w, h) * 0.62;
      for (const s of stars) {
        s.z -= speed;
        if (s.z <= 0.001) { Object.assign(s, make(false)); continue; }
        const sx = cx + (s.nx / s.z) * fov, sy = cy + (s.ny / s.z) * fov;
        if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) { Object.assign(s, make(false)); continue; }
        const near = Math.pow(1 - s.z, 1.6);
        const bright = Math.min(1, near * 2.2);
        const r = Math.max(0.25, s.size * near * 2);
        if (speed > 0.003) {
          const pz = Math.min(1, s.z + speed * 5);
          const px = cx + (s.nx / pz) * fov, py = cy + (s.ny / pz) * fov;
          const grad = ctx.createLinearGradient(px, py, sx, sy);
          grad.addColorStop(0, `hsla(${s.hue},90%,70%,0)`);
          grad.addColorStop(1, `hsla(${s.hue},92%,82%,${bright * 0.6})`);
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.strokeStyle = grad; ctx.lineWidth = r * 0.5; ctx.stroke();
        }
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${s.hue},92%,88%,${bright})`; ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" data-testid="r2-companion-warp" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.55, zIndex: 0 }} />;
}

// ── Lightweight inline markdown ────────────────────────────────────────────────

function renderInline(text: string): JSX.Element[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter((p) => p !== '');
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i} style={{ fontWeight: 700, color: '#e6fffb' }}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={i} style={{ fontFamily: 'monospace', fontSize: '0.9em', background: 'rgba(37,181,168,0.12)', border: '1px solid rgba(37,181,168,0.22)', borderRadius: 5, padding: '1px 5px', color: '#7ee0d6' }}>{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
}

function MarkdownLite({ text }: { text: string }): JSX.Element {
  return (
    <>
      {text.split('\n').map((line, i) => {
        if (line.trim() === '') return <div key={i} style={{ height: 5 }} />;
        const bullet = /^\s*[•\-*]\s+/.test(line);
        const content = bullet ? line.replace(/^\s*[•\-*]\s+/, '') : line;
        if (bullet) return <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}><span style={{ color: '#5eead4', flexShrink: 0, lineHeight: 1.6 }}>•</span><span>{renderInline(content)}</span></div>;
        return <div key={i}>{renderInline(content)}</div>;
      })}
    </>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export function R2Companion(): JSX.Element {
  const initial = useRef<Persisted>(loadState());
  const [open, setOpen] = useState(initial.current.open);
  const [parked, setParked] = useState(initial.current.parked);
  const [reveal, setReveal] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(initial.current.turns);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);

  const convIdRef = useRef(initial.current.convId);
  const abortRef = useRef<AbortController | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Persist relevant state across reloads (skip mid-stream to avoid per-chunk writes).
  useEffect(() => {
    if (streaming) return;
    saveState({ open, parked, convId: convIdRef.current, turns });
  }, [open, parked, turns, streaming]);

  // Fire the global Warp Drive event alongside our scoped canvas.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: streaming } }));
  }, [streaming]);
  useEffect(() => () => { window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: false } })); abortRef.current?.abort(); }, []);

  // Auto-scroll transcript.
  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [turns, open]);

  const streamWeb = useCallback(async (msgs: { role: string; content: string }[]) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const licenseKey = (() => { try { return localStorage.getItem('rcd2.license_key'); } catch { return null; } })();
    try {
      const resp = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: licenseKey ?? null, email: null, messages: msgs, session_id: convIdRef.current }),
        signal: abortRef.current.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') break outer;
          try {
            const p = JSON.parse(raw) as { content?: string };
            if (p.content) setTurns((cur) => {
              const last = cur[cur.length - 1];
              if (!last || last.role !== 'assistant' || !last.streaming) return cur;
              return [...cur.slice(0, -1), { ...last, content: last.content + p.content }];
            });
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Unknown';
      setTurns((cur) => {
        const last = cur[cur.length - 1];
        if (!last || last.role !== 'assistant') return cur;
        return [...cur.slice(0, -1), { ...last, content: `[Emmy encountered an error: ${msg}]`, streaming: false }];
      });
    } finally {
      setStreaming(false);
      setTurns((cur) => {
        const last = cur[cur.length - 1];
        if (!last || last.role !== 'assistant' || !last.streaming) return cur;
        return [...cur.slice(0, -1), { ...last, streaming: false }];
      });
    }
  }, []);

  const send = useCallback((text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    playClick(); haptic();
    setInput('');
    const userTurn: ChatTurn = { id: crypto.randomUUID(), role: 'user', content: msg };
    const aiTurn: ChatTurn = { id: crypto.randomUUID(), role: 'assistant', content: '', streaming: true };
    const history = turns.map((t) => ({ role: t.role, content: t.content }));
    const allMsgs = [{ role: 'system', content: EMMY_SYSTEM_PROMPT }, ...history, { role: 'user', content: msg }];
    setTurns((cur) => [...cur, userTurn, aiTurn]);
    setStreaming(true);
    setTimeout(() => inputRef.current?.focus(), 0);
    void streamWeb(allMsgs);
  }, [input, streaming, turns, streamWeb]);

  function reset(): void {
    if (streaming) return;
    playNav(); haptic();
    abortRef.current?.abort();
    convIdRef.current = crypto.randomUUID();
    setTurns([]); setInput('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function toggleOpen(): void {
    playClick(); haptic();
    setOpen((o) => {
      const next = !o;
      // Trigger the cinematic Global Intelligence Reveal when opening.
      setReveal(next);
      return next;
    });
  }
  function togglePark(): void {
    playNav(); haptic();
    setParked((p) => !p);
  }

  // ── Closed: floating action button ──────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        data-testid="r2-companion-fab"
        onClick={toggleOpen}
        aria-label={`Chat with ${EMMY_NAME}`}
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 60,
          width: 60, height: 60, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #134e4a, #06201d)',
          border: '1.5px solid rgba(37,181,168,0.6)',
          boxShadow: '0 0 26px rgba(37,181,168,0.35), 0 8px 24px rgba(0,0,0,0.4)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'r2FabPulse 2.6s ease-in-out infinite',
        }}
      >
        <style>{`
          @keyframes r2FabPulse { 0%,100%{ box-shadow:0 0 20px rgba(37,181,168,0.28),0 8px 24px rgba(0,0,0,0.4);} 50%{ box-shadow:0 0 34px rgba(37,181,168,0.5),0 8px 24px rgba(0,0,0,0.4);} }
        `}</style>
        <span style={{ fontSize: 26, fontWeight: 900, color: '#5eead4', letterSpacing: '-0.02em', userSelect: 'none' }}>E</span>
        <span style={{ position: 'absolute', top: 4, right: 6, width: 9, height: 9, borderRadius: '50%', background: '#5eead4', boxShadow: '0 0 8px #5eead4', border: '1px solid #06201d' }} />
      </button>
    );
  }

  // ── Open: dockable panel ─────────────────────────────────────────────────────
  const sideDocked = parked || reveal;
  const panelStyle: React.CSSProperties = sideDocked
    ? { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(380px, 100vw)', height: '100vh', borderRadius: 0, zIndex: 60 }
    : { position: 'fixed', right: 20, bottom: 20, width: 'min(380px, calc(100vw - 24px))', height: 'min(560px, calc(100vh - 100px))', borderRadius: 18, zIndex: 60 };

  const empty = turns.length === 0;

  return (
    <>
      {reveal && (
        <GlobalIntelligenceReveal onClose={() => setReveal(false)} sidePanelWidth={sideDocked ? 380 : 0} />
      )}
      <div
        data-testid="r2-companion-panel"
        data-parked={sideDocked ? 'true' : 'false'}
        style={{
          ...panelStyle,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'rgba(6, 14, 24, 0.92)',
          backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
          border: '1px solid rgba(37,181,168,0.3)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.55), 0 0 40px rgba(37,181,168,0.08)',
          animation: reveal ? 'r2SlideIn 0.5s cubic-bezier(0.22,1,0.36,1) both' : 'r2PanelIn 0.28s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <style>{`
          @keyframes r2PanelIn { from{opacity:0; transform: translateY(16px) scale(0.98);} to{opacity:1; transform:none;} }
          @keyframes r2SlideIn { from{opacity:0; transform: translateX(100%);} to{opacity:1; transform:none;} }
          @keyframes r2Bubble { from{opacity:0; transform: translateY(8px);} to{opacity:1; transform:none;} }
        `}</style>

      {/* Header */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid rgba(37,181,168,0.16)', background: 'linear-gradient(180deg, rgba(13,42,38,0.4) 0%, transparent 100%)', position: 'relative', zIndex: 2 }}>
        <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%, #134e4a, #0d2b27)', border: `1px solid rgba(37,181,168,${streaming ? 0.8 : 0.5})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: streaming ? '0 0 16px rgba(37,181,168,0.5)' : '0 0 8px rgba(37,181,168,0.22)', transition: 'box-shadow 0.4s' }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: '#5eead4' }}>E</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#e6fffb', letterSpacing: '-0.01em' }}>{EMMY_NAME}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#5eead4' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5eead4', boxShadow: '0 0 6px #5eead4', animation: streaming ? 'r2FabPulse 0.9s ease-in-out infinite' : 'none' }} />
            {streaming ? 'Thinking…' : 'Online'}
          </div>
        </div>
        <button type="button" data-testid="r2-companion-reset" onClick={reset} disabled={streaming} title="New conversation"
          style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid rgba(120,140,160,0.35)', color: 'rgba(160,180,200,0.7)', cursor: streaming ? 'not-allowed' : 'pointer', opacity: streaming ? 0.4 : 1 }}>
          <RotateCcw style={{ width: 12, height: 12 }} strokeWidth={1.8} />
        </button>
        <button type="button" data-testid="r2-companion-park-toggle" onClick={togglePark} title={parked ? 'Float bottom-right' : 'Park on side'}
          style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: parked ? 'rgba(37,181,168,0.14)' : 'transparent', border: '1px solid rgba(37,181,168,0.3)', color: '#5eead4', cursor: 'pointer' }}>
          {parked ? <PanelBottomClose style={{ width: 13, height: 13 }} strokeWidth={1.8} /> : <PanelRightClose style={{ width: 13, height: 13 }} strokeWidth={1.8} />}
        </button>
        <button type="button" data-testid="r2-companion-minimize" onClick={toggleOpen} title="Minimize"
          style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid rgba(120,140,160,0.35)', color: 'rgba(160,180,200,0.7)', cursor: 'pointer' }}>
          <Minus style={{ width: 14, height: 14 }} strokeWidth={2} />
        </button>
      </div>

      {/* Transcript + scoped warp */}
      <div ref={transcriptRef} data-testid="r2-companion-transcript" style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        <MiniWarp active={streaming} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 14px', minHeight: '100%' }}>
          {empty ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
              <p style={{ fontSize: 12.5, lineHeight: 1.65, color: 'rgba(210,230,228,0.8)' }}>{EMMY_GREETING}</p>
              {[
                'Estimate a Cat6A run from MDF to IDF-2 (180 ft).',
                'What TR size do I need for a 250-node floor?',
                'Flag any cabling runs over the 90 m TIA-568 limit.',
              ].map((q) => (
                <button key={q} type="button" data-testid="r2-companion-starter" onClick={() => send(q)}
                  style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 12, background: 'rgba(13,42,38,0.5)', border: '1px solid rgba(37,181,168,0.22)', cursor: 'pointer', color: 'rgba(215,230,230,0.82)', fontSize: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Sparkles style={{ width: 11, height: 11, color: '#5eead4' }} strokeWidth={2.2} /> {q}</span>
                </button>
              ))}
            </div>
          ) : turns.map((t) => {
            const isUser = t.role === 'user';
            const isEmpty = !t.content && t.streaming;
            return (
              <div key={t.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', gap: 3, maxWidth: '88%', alignSelf: isUser ? 'flex-end' : 'flex-start', animation: 'r2Bubble 0.22s ease' }}>
                <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: isUser ? 'rgba(180,195,215,0.5)' : '#5eead4', padding: isUser ? '0 3px 0 0' : '0 0 0 3px' }}>{isUser ? 'You' : EMMY_NAME}</span>
                <div style={isUser ? {
                  background: 'rgba(30,41,59,0.85)', border: '1px solid rgba(99,120,150,0.35)', borderRadius: '16px 16px 3px 16px', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.6, color: 'rgba(225,232,248,0.92)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                } : {
                  background: 'rgba(13,42,38,0.82)', border: '1px solid rgba(37,181,168,0.28)', borderRadius: '16px 16px 16px 3px', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.6, color: 'rgba(220,240,238,0.9)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {isEmpty
                    ? <span style={{ opacity: 0.6, fontSize: 11.5, fontStyle: 'italic' }}>{EMMY_NAME} is thinking…</span>
                    : <><MarkdownLite text={t.content} />{t.streaming && <span style={{ display: 'inline-block', width: 6, height: 12, background: '#5eead4', borderRadius: 2, marginLeft: 3, verticalAlign: 'text-bottom', animation: 'r2FabPulse 0.7s step-end infinite' }} />}</>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Input */}
      <div style={{ flexShrink: 0, padding: '10px 12px 12px', borderTop: '1px solid rgba(37,181,168,0.14)', background: 'linear-gradient(0deg, rgba(4,10,22,0.6) 0%, transparent 100%)', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <textarea
            ref={inputRef}
            data-testid="r2-companion-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={streaming ? `${EMMY_NAME} is responding…` : `Ask ${EMMY_NAME}…`}
            rows={1}
            disabled={streaming}
            style={{ flex: 1, background: 'rgba(10,22,36,0.85)', border: streaming ? '1.5px solid rgba(37,181,168,0.55)' : '1.5px solid rgba(50,80,100,0.5)', borderRadius: 14, padding: '10px 13px', fontSize: 12.5, color: 'rgba(225,232,248,0.92)', resize: 'none', outline: 'none', lineHeight: 1.5, maxHeight: 96, fontFamily: 'inherit' }}
          />
          {streaming ? (
            <button type="button" data-testid="r2-companion-stop" onClick={() => { abortRef.current?.abort(); setStreaming(false); }}
              style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5', cursor: 'pointer' }}>
              <StopCircle style={{ width: 16, height: 16 }} />
            </button>
          ) : (
            <button type="button" data-testid="r2-companion-send" onClick={() => send()} disabled={!input.trim()}
              style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(13,90,80,0.9), rgba(5,55,50,0.95))', border: '1.5px solid rgba(37,181,168,0.55)', color: '#5eead4', cursor: input.trim() ? 'pointer' : 'not-allowed', opacity: input.trim() ? 1 : 0.45, boxShadow: input.trim() ? '0 0 16px rgba(37,181,168,0.22)' : 'none' }}>
              <Send style={{ width: 15, height: 15 }} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
